<script setup lang="tsx">
import type { DocumentReference, Document } from '@/models/api-generated'
import IconButton from '../shared/IconButton.vue'
import Modal from '../shared/Modal.vue'
import DocumentFolders from '../document/DocumentFolders.vue'
import { computed, ref, watch } from 'vue'
import { defaultInstance, hasPermission } from '@/services/utils'
import { PermDocumentAdd, PermDocumentRead } from '@/models/enums-generated'
import { truncate } from '@/services/utils'

const model = defineModel<DocumentReference[]>({ required: true })
const showFolderModal = ref(false)
const canAddDocument = computed(() => hasPermission(PermDocumentAdd))
const selectedFileRef = ref<Document>()
const currentRefIndex = ref<number>()
const canView = computed(() => hasPermission(PermDocumentRead))
const preselectDocument = ref<Document>()
const pendingSelection = ref<Document>()

const props = defineProps<{
	canEdit?: boolean
	ownerId?: number
	ownerType: string
}>()

function addRef() {
	model.value.push({
		id: 0,
		title: '',
		ownerId: props.ownerId ? props.ownerId : 0,
		ownerType: props.ownerType,
		documentId: 0,
		document: {
			id: 0,
			path: '/',
			length: 0,
			name: '',
			referenceCount: 0,
			author: sessionMap.user,
			instance: defaultInstance(),
		},
	})
}

function removeRef(index: number) {
	model.value.splice(index, 1)
}

function selectFile() {
	selectedFileRef.value = pendingSelection.value
	showFolderModal.value = false
}

function openDocumentModal(index: number | undefined, doc?: Document) {
	currentRefIndex.value = index
	showFolderModal.value = true
	if (doc) preselectDocument.value = doc
}

watch(selectedFileRef, (file) => {
	if (!file || currentRefIndex.value === undefined) return
	model.value[currentRefIndex.value].documentId = file.id
	model.value[currentRefIndex.value].document = file
	selectedFileRef.value = undefined
	showFolderModal.value = false
	currentRefIndex.value = undefined
})
</script>

<template>
	<div class="mb-3">
		<div class="d-flex align-items-center mb-2">
			<strong>{{ $t('reference.document') }}</strong>
		</div>
		<template v-if="!model?.length">
			<div>-</div>
		</template>
		<template v-else>
			<div v-for="(ref, index) in model" :key="index" class="mb-2">
				<div class="d-flex flex-column gap-1">
					<template v-if="props.canEdit">
						<div class="d-flex gap-2 align-items-start w-100">
							<input
								:data-test="ownerType + '-document-select-' + ownerId"
								type="text"
								class="form-control"
								:placeholder="$t('reference.selectFile')"
								:value="ref.document?.name || ''"
								readonly
								@click="openDocumentModal(index)"
							/>
							<IconButton
								:data-test="ownerType + '-delete-reference-' + ownerId"
								class="bi bi-trash"
								:title="$t('misc.delete')"
								@click="removeRef(index)"
								size="fs-5"
							/>
						</div>
						<input
							:data-test="ownerType + '-reference-title-' + ownerId"
							type="text"
							class="form-control"
							:placeholder="$t('reference.title')"
							v-model="ref.title"
						/>
					</template>
					<template v-else>
						<div class="d-flex align-items-start gap-2" @click="openDocumentModal(undefined, ref.document)">
							<i class="bi bi-file-earmark-text text-secondary" v-one-tooltip="ref.document.name"></i>
							<span>{{ ref.title || truncate(ref.document?.name) }}</span>
						</div>
					</template>
				</div>
				<hr v-if="index !== model.length - 1" class="my-2 border-top border-secondary opacity-25" />
			</div>
		</template>
		<button
			v-if="canAddDocument && props.canEdit && canView"
			:data-test="ownerType + '-document-reference-' + ownerId"
			type="button"
			class="btn btn-outline-primary btn-sm"
			@click="addRef()"
		>
			<i class="bi bi-plus-circle me-1"></i> {{ $t('reference.add') }}
		</button>
		<Modal v-model="showFolderModal" class="modal-xl">
			<template #header>
				<div class="row">
					<div class="ellipsis col-11">
						{{ $t('document.label') }}
					</div>
				</div>
			</template>
			<template #body>
				<DocumentFolders
					@file-selected="pendingSelection = $event"
					:show-select="currentRefIndex !== undefined"
					:preselect-document="preselectDocument"
					selection-type="document"
				/>
			</template>
			<template #footer>
				<button class="btn btn-secondary" @click="showFolderModal = false">
					{{ $t('misc.cancel') }}
				</button>
				<button class="btn btn-primary" :disabled="!pendingSelection" @click="selectFile">
					{{ $t('misc.confirm') }}
				</button>
			</template>
		</Modal>
	</div>
</template>

<style scoped>
:deep(.modal-dialog.modal-xl) {
	max-width: 75vw;
}
</style>
